import { Field } from '@headlessui/react';
import React, { FC } from 'react';
import { Control, useWatch } from 'react-hook-form';
import { twJoin } from 'tailwind-merge';

import { Checkbox } from '../../../../../../components/checkbox/index';
import { Tooltip } from '../../../../../../components/tooltip/index';
import { QueryKey } from '../../../../../../constants/index';
import { ScenarioHooks, useInvalidateQueries } from '../../../../../../hooks/index';
import { ClassConversionTask } from '../../../../../../types/scenario-modeling.types';
import { toNumber } from '../../../../../../utils/to-number.util';
import { useScenarioCapTableStore } from '../../../../cap-table.store';
import { useScenarioStore } from '../../../../scenario-modeling.store';
import { LiquidityFormSchema } from '../../../liquidity-validation';

export type GrantItemProps = {
  control: Control<LiquidityFormSchema>;
  isChecked: boolean;
  item: ClassConversionTask;
  scenarioId: string;
};

export const ClassConversionItem: FC<GrantItemProps> = ({
  isChecked,
  item,
  control,
  scenarioId,
}) => {
  const { id } = item;
  const { classConversion } = useWatch<LiquidityFormSchema>({ control });
  const currentClassConversion = classConversion?.classConversionItems?.find(
    ({ classConversionTaskId }) => classConversionTaskId === id,
  );

  const { pointerBlockedStyles } = useScenarioStore();

  const { scheduleUpdate } = useScenarioCapTableStore();
  const { createScenarioClassConversion, isPending: isCreating } =
    ScenarioHooks.useCreateScenarioClassConversion();
  const { deleteScenarioClassConversion, isPending: isDeleting } =
    ScenarioHooks.useDeleteScenarioClassConversion();
  const { invalidateQuery } = useInvalidateQueries(
    QueryKey.GET_SCENARIO_CLASS_CONVERSIONS,
    QueryKey.GET_SCENARIO,
  );

  const handleSelect = () => {
    scheduleUpdate(scenarioId);
    createScenarioClassConversion(
      {
        scenarioId,
        conversionId: id,
      },
      { onSuccess: () => invalidateQuery() },
    );
  };

  const handleRemove = () => {
    if (currentClassConversion?.id) {
      scheduleUpdate(scenarioId);
      deleteScenarioClassConversion(
        { scenarioId, id: currentClassConversion.id },
        { onSuccess: () => invalidateQuery() },
      );
    }
  };

  return (
    <>
      <div
        className={twJoin('flex cursor-pointer items-center justify-center', pointerBlockedStyles)}
      >
        <Field className="flex items-center">
          <Checkbox
            checked={isChecked}
            disabled={isCreating || isDeleting}
            id={id}
            onChange={(checked) => {
              checked ? handleSelect() : handleRemove();
            }}
          />
        </Field>
      </div>
      <div className="px-2 py-[26px]">
        <Tooltip
          childrenWrapperClassName="max-w-[300px] pt-[2px]"
          content={item?.stakeholder?.fullName}
        >
          <span className="w-full truncate text-nowrap text-sm font-[450] text-gray-700">
            {item?.stakeholder?.fullName}
          </span>
        </Tooltip>
      </div>
      <div className="px-2 py-[26px]">
        <Tooltip content={item?.shareClass?.name}>
          <span className="w-full text-nowrap text-sm font-[450] text-gray-700">
            {item?.shareClass?.name}
          </span>
        </Tooltip>
      </div>

      <div className="px-2 py-[26px]">
        <span className="w-full text-sm font-[450] text-gray-700">
          {item?.preferredShares?.toLocaleString('en-US')}
        </span>
      </div>
      <div className="px-2 py-[26px]">
        <span className="w-full text-sm font-[450] text-gray-700">
          {(currentClassConversion?.conversion || item?.conversionRatio)?.toLocaleString('en-US')}
        </span>
      </div>
      <div className="px-2 py-[26px]">
        <span className="w-full text-sm font-[450] text-gray-700">
          {currentClassConversion
            ? Math.round(
                toNumber(currentClassConversion?.convertedShares) *
                  toNumber(currentClassConversion?.conversion),
              )?.toLocaleString('en-US')
            : item?.preferredShares?.toLocaleString('en-US')}
        </span>
      </div>
    </>
  );
};
